/**
 * Central navigation configuration.
 *
 * All nav items live here. The sidebar reads this config and filters sections
 * at render-time based on the current user's roles and organisation plan.
 *
 * To add a new nav item: add it to BASE_NAV_SECTIONS with the appropriate
 * `roles` and `plans` constraints. No other file needs to change.
 */

import { IconType } from "react-icons"
import {
  FaHome,
  FaBriefcase,
  FaUsers,
  FaClipboardList,
  FaCreditCard,
  FaBuilding,
  FaUser,
  FaCog,
  FaFileWord,
  FaFolderOpen,
  FaLink,
  FaWallet,
  FaEnvelope,
} from "react-icons/fa"
import { AppRole } from "@/modules/auth/constants/app-role";
import { PlanType } from "@/modules/organizations/types/plan-type";

// ── Access presets ─────────────────────────────────────────────────────────
// Single source of truth for plan/role constraints.
// Used by both nav filtering (buildNavSections) and route guards (App.tsx).

// ── Types ──────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string
  icon: IconType
  path?: string
  submenu?: NavItem[]
}

export interface NavSection {
  title?: string
  /** Small icon rendered next to the section title */
  icon?: IconType
  items: NavItem[]
  /** If set, section is only shown when the org plan is in this list */
  plans?: PlanType[]
  /** If set, section is only shown when the user has at least one of these roles */
  roles?: AppRole[]
}

// ── Base config (static) ───────────────────────────────────────────────────

const BASE_NAV_SECTIONS: NavSection[] = [
  // ── Overview (org roles) ─────────────────────────────────────────────
  {
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    items: [{ label: "Overview", icon: FaHome, path: "/dashboard" }],
  },

  // ── Recruitment ──────────────────────────────────────────────────────
  {
    title: "Recruitment",
    icon: FaBriefcase,
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    plans: [PlanType.Recruitment, PlanType.FullHiring],
    items: [
      {
        label: "Jobs",
        icon: FaBriefcase,
        submenu: [
          {
            label: "Vacancies",
            icon: FaBriefcase,
            path: "/recruitment/vacancies",
          },
        ],
      },
    ],
  },

  // ── Onboarding ───────────────────────────────────────────────────────
  {
    title: "Onboarding",
    icon: FaClipboardList,
    roles: [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ],
    plans: [PlanType.Onboarding, PlanType.FullHiring],
    items: [
      {
        label: "Employees",
        icon: FaUsers,
        submenu: [
          {
            label: "All Employees",
            icon: FaUsers,
            path: "/onboarding/employees",
          },
          {
            label: "Offboarding",
            icon: FaUsers,
            path: "/onboarding/offboarding",
          },
        ],
      },
      {
        label: "Workflows",
        icon: FaClipboardList,
        submenu: [
          {
            label: "Workflow Manager",
            icon: FaClipboardList,
            path: "/onboarding/workflows",
          },
          {
            label: "Live Workflows",
            icon: FaClipboardList,
            path: "/onboarding/workflows/live",
          },
        ],
      },
    ],
  },

  // ── Documents (admin only) ───────────────────────────────────────────
  {
    title: "Documents",
    icon: FaFolderOpen,
    roles: [AppRole.OrganizationOwner, AppRole.SuperAdmin],
    items: [
      {
        label: "All Documents",
        icon: FaFolderOpen,
        path: "/admin/documents",
      },
    ],
  },

  // ── Employee ─────────────────────────────────────────────────────────
  {
    title: "Onboarding",
    icon: FaFileWord,
    roles: [AppRole.Employee],
    items: [
      { label: "Dashboard", icon: FaHome, path: "/employee/dashboard" },
      {
        label: "Onboarding",
        icon: FaClipboardList,
        path: "/employee/onboarding",
      },
      {
        label: "Offboarding",
        icon: FaBriefcase,
        path: "/employee/offboarding",
      }, {
        label: "Documents",
        icon: FaFileWord,
        path: "/employee/documents",
      },
    ],
  },
]

// ── Builder ────────────────────────────────────────────────────────────────

export interface NavBuildOptions {
  currentPlan?: PlanType | string;
  currentOrgId?: string | null;
  userRoles: AppRole[];
}

/**
 * Returns the filtered, fully-resolved nav sections for the current user.
 * Call this once per render cycle (in the sidebar component).
 */
export function buildNavSections(options: NavBuildOptions): NavSection[] {
  const {
    currentPlan,
    currentOrgId,
    userRoles,
  } = options;

  const sections: NavSection[] = BASE_NAV_SECTIONS
    // Filter by role
    .filter((s) => !s.roles || s.roles.some((r) => userRoles.includes(r)))
    // Filter by plan
    .filter(
      (s) =>
        !s.plans ||
        (!!currentPlan && s.plans.includes(currentPlan as PlanType)),
    )

  // Settings section: dynamic because org-profile path depends on currentOrgId.
  // Shown only for org roles.
  const isOrgRole = userRoles.some((r) =>
    [
      AppRole.OrganizationOwner,
      AppRole.RegisteredUser,
      AppRole.TeamMember,
    ].includes(r),
  );

  if (isOrgRole) {
    const settingsItems: NavItem[] = [];

    if (currentOrgId) {
      const isOwner = userRoles.includes(AppRole.OrganizationOwner);

      if (isOwner) {
        settingsItems.push(
          {
            label: "Organisation Profile",
            icon: FaBuilding,
            path: `/account/profile/organization/${currentOrgId}`,
          },
          {
            label: "User Management",
            icon: FaUsers,
            path: "/settings/users",
          }
        );
      }
    }

    settingsItems.push(
      {
        label: "Billing & Subscriptions",
        icon: FaCreditCard,
        path: "/billing",
      },
      { label: "Wallet", icon: FaWallet, path: "/wallet" },
      { label: "Integrations", icon: FaLink, path: "/integrations" },
      { label: "Email Templates", icon: FaEnvelope, path: "/settings/email-templates" },
      { label: "My Profile", icon: FaUser, path: "/account/profile" },
    );

    sections.push({ title: "Settings", icon: FaCog, items: settingsItems });
  }

  return sections;
}
